import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, doc, onSnapshot } from '@battleops/localstore';
import { db } from '@battleops/client/src/data/store';
import { commitWrites } from '@battleops/client/src/data/commit';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { deriveAttached, nextOccurrences, commands, localDateStr, answeredElsewhere, answerWrites, targetOpenLabel, reminderDefaults, runsOn } from '@battleops/shared';
import { getSync } from '@battleops/client/src/session/boot';
import { useRoutines } from '@battleops/client/src/context/ProcessContext';
import { openRoutineMode } from '../lib/routineMode';
import { useOpenTarget, useDescribeTarget } from '../lib/targets';
import { useStartTask } from '../lib/actions';
import Icon from './Icon';
import { usePref } from '../hooks';
import { useApprovals } from '@battleops/client/src/data/useApprovals';
import s from './Ringer.module.css';

const TICK_MS = 10000;
const RINGING_SYNC_MS = 8000;      // while something rings here, pull often so an answer on the phone closes it fast
const GRACE_MS = 10 * 60000;        // a tab opened a little late still rings what just came due
const rungKey = (r) => `ops.web.rang.${r.id}|${r.at}`;
// The shared contract's row for one ringing occurrence (answeredElsewhere reads these).
const liveRow = (r) => ({ key: `${r.id}|${r.at}`, reminderId: r.virtual ? null : (r.occurrenceOf || r.id), virtualId: r.virtual ? r.id : null, at: r.at });

// The desk rings too. The same reminders the phone arms (stored ones, and those derived from events, blocks and
// tasks — shared/reminders.js) are watched here; when one comes due: 'fullscreen' / 'alarm' styles take over the
// screen, 'banner' slides in at the corner — each with Done / Snooze / Open, a chime until answered, and a desktop
// notification when the tab is in the background. One ring per occurrence across tabs and reloads.
export default function Ringer() {
  const { tasks, updateTask, setTaskDone } = useTasks();
  const openTarget = useOpenTarget();
  const describe = useDescribeTarget();
  const startTask = useStartTask();
  const [chime] = usePref('ring.chime', true);
  const { rawRoutines, allRuns, uid, startRoutine, skipRoutine } = useRoutines();
  const src = useRef({ reminders: [], events: [], blocks: [], settings: null, answers: [] });
  src.current.routines = rawRoutines; src.current.runs = allRuns;
  // A routine ring whose run was started / skipped / done since (here or on the phone) is answered.
  const routineAnswered = (r) => r?.routineId && runsOn(src.current.runs, r.routineId, r.date, uid).some((x) => (x.n || 1) === (r.n || 1) && x.status !== 'due');
  const [tick, setTick] = useState(0);
  const [queue, setQueue] = useState([]);
  const snoozed = useRef([]);          // local re-rings for derived reminders (events / blocks) — they have no doc

  useEffect(() => {
    const read = (key) => (snap) => { src.current[key] = snap.docs.map((d) => ({ id: d.id, ...d.data() })); setTick((t) => t + 1); };
    const offs = [
      onSnapshot(collection(db, 'reminders'), read('reminders')),
      onSnapshot(collection(db, 'calendarEvents'), read('events')),
      onSnapshot(collection(db, 'timeBlocks'), read('blocks')),
      onSnapshot(doc(db, 'meta', 'settings'), (snap) => { src.current.settings = snap.exists() ? snap.data() : null; }),
      onSnapshot(collection(db, 'reminderAnswers'), read('answers')),
    ];
    const iv = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => { offs.forEach((off) => off()); clearInterval(iv); };
  }, []);

  // Settings → Reminders → "Ring a test": a pretend reminder, rung here only.
  useEffect(() => {
    const test = (e) => setQueue((q) => [...q, { id: `test-${Date.now()}`, virtual: true, title: 'Test reminder', body: 'This is how reminders ring on this computer.', at: Date.now(), style: e.detail?.style || 'banner', link: e.detail?.link || null, source: 'test' }]);
    window.addEventListener('ops:test-ring', test);
    return () => window.removeEventListener('ops:test-ring', test);
  }, []);

  // What came due since the last look → ring it once.
  useEffect(() => {
    const now = Date.now();
    const { reminders, events, blocks, settings } = src.current;
    const from = localDateStr(now - GRACE_MS); const to = localDateStr(now);
    const attached = deriveAttached({ events, blocks, tasks, settings, from, to, routines: src.current.routines || [], routineRuns: src.current.runs || [], uid });
    const due = nextOccurrences({ reminders, attached, fromMs: now - GRACE_MS, toMs: now });
    const local = snoozed.current.filter((r) => r.at <= now);
    snoozed.current = snoozed.current.filter((r) => r.at > now);
    const fresh = [];
    // Already answered on another device (done, snoozed, turned off) → don't ring it here.
    const gone = new Set(answeredElsewhere([...due, ...local].map(liveRow), { reminders, tasks, answers: src.current.answers }));
    for (const r of [...due, ...local]) {
      if (r.status === 'done' || r.status === 'cancelled' || gone.has(liveRow(r).key) || routineAnswered(r)) continue;
      const k = rungKey(r);
      try { if (localStorage.getItem(k)) continue; localStorage.setItem(k, String(now)); } catch { /* private mode: this tab only */ }
      fresh.push(r);
      const stored = !r.virtual && (r.occurrenceOf || r.id);
      if (stored && r.status === 'scheduled') handle(stored, 'fired');
    }
    if (fresh.length) setQueue((q) => [...q, ...fresh.filter((r) => !q.some((x) => x.id === r.id && x.at === r.at))]);
  }, [tick, tasks]);

  // Answered on another device while it rings here (the phone's Done / snooze / turn-off) → close this copy too.
  useEffect(() => {
    setQueue((q) => {
      if (!q.length) return q;
      const gone = new Set(answeredElsewhere(q.map(liveRow), { reminders: src.current.reminders, tasks, answers: src.current.answers }));
      const keep = q.filter((r) => !gone.has(liveRow(r).key) && !routineAnswered(r));
      return keep.length === q.length ? q : keep;
    });
  }, [tick, tasks, allRuns]);

  // Ringing here: pull every few seconds (the tab may be in the background), so the phone's answer reaches it quickly.
  const ringing = queue.length > 0;
  useEffect(() => {
    if (!ringing) return undefined;
    const iv = setInterval(() => { getSync()?.syncNow?.().catch(() => {}); }, RINGING_SYNC_MS);
    return () => clearInterval(iv);
  }, [ringing]);

  // A ring with no doc of its own (event / block / routine) answered here → recorded, so the phone closes its copy.
  const answer = (r, action, until = null) => {
    if (!r?.virtual || r.taskReminder || r.source === 'test') return;
    const w = answerWrites({ virtualId: r.id, action, until, title: r.title, link: r.link, at: r.at, answers: src.current.answers, now: Date.now() });
    if (w.length) commitWrites(w).catch((e) => console.error('reminder answer', e));
  };

  const handle = (id, action, minutes) => {
    try {
      const r = commands.handleReminder({ reminders: src.current.reminders, now: Date.now() }, { id, action, minutes });
      commitWrites(r.writes).catch((e) => console.error('reminder', action, e));
    } catch (e) { console.error(e); }
  };

  const current = queue[0] || null;
  const close = useCallback(() => setQueue((q) => q.slice(1)), []);
  const storedId = current && !current.virtual ? (current.occurrenceOf || current.id) : null;
  const task = current?.taskId ? tasks.find((t) => t.id === current.taskId) : current?.link?.type === 'task' ? tasks.find((t) => t.id === current.link.id) : null;

  const done = () => {
    if (storedId) handle(storedId, 'done'); else answer(current, 'done');
    if (current.taskReminder && task) setTaskDone(task.id, true);
    close();
  };
  const snooze = (min) => {
    const at = Date.now() + min * 60000;
    if (storedId) handle(storedId, 'snooze', min);
    else if (current.taskReminder && task) updateTask({ id: task.id, remindTime: at, scheduledDate: localDateStr(at) });
    else { snoozed.current.push({ ...current, at }); answer(current, 'snooze', at); }
    close();
  };
  // Turned off without an answer: recorded, so every other device closes its copy (shared markHandled 'dismiss' /
  // task.remindDismissedAt).
  const dismiss = () => {
    if (storedId) handle(storedId, 'dismiss');
    else if (current.taskReminder && task) updateTask({ id: task.id, remindDismissedAt: current.at });
    else answer(current, 'dismiss');
    close();
  };
  const open = () => {
    openTarget(current.source === 'approval' && !current.link ? { type: 'approval', id: current.id } : current.link);
    if (storedId) handle(storedId, 'open');
    close();
  };
  // A reminder about a task can start it straight from the ring (clocks you in if you're not).
  const linkedTask = task || (current?.link?.type === 'task' ? tasks.find((x) => x.id === current.link.id) : null);
  const start = linkedTask && linkedTask.status !== 'done' ? () => { startTask(linkedTask); if (storedId) handle(storedId, 'done'); else if (current.taskReminder) updateTask({ id: linkedTask.id, remindDismissedAt: current.at }); close(); } : null;

  if (!current) return null;
  // Approvals ring as ONE stacked alert — every waiting one in the queue, each approved / denied on its own, or all
  // at once — instead of one takeover after another (Lj, 2026-10-08: "they should stack").
  if (current.source === 'approval') {
    const stack = queue.filter((r) => r.source === 'approval');
    const closeIds = (ids) => setQueue((q) => q.filter((r) => !(r.source === 'approval' && ids.includes(r.id))));
    return <ApprovalStack items={stack} chime={chime} onClose={closeIds}
      onReview={() => { openTarget({ type: 'approval', id: stack[0].approval?.id || stack[0].link?.id || stack[0].id }); closeIds(stack.map((r) => r.id)); }}
      onDismiss={() => { stack.forEach((r) => { if (!r.virtual) handle(r.occurrenceOf || r.id, 'dismiss'); }); closeIds(stack.map((r) => r.id)); }} />;
  }
  if (current.routineId) {
    const slot = { ...(current.date === localDateStr(Date.now()) ? { date: current.date, n: current.n } : {}) };
    const go = () => { close(); try { startRoutine(current.routineId, { ...slot, trigger: { type: 'ring', at: Date.now() } }); openRoutineMode(); } catch (e) { console.warn(e); } };
    const skip = () => { close(); answer(current, 'skip'); try { skipRoutine(current.routineId, { date: current.date, n: current.n, reason: 'Not today' }); } catch (e) { console.warn(e); } };
    return <Alert r={current} more={queue.length - 1} routine={current.look || {}} onStartRoutine={go} onSkipRoutine={skip} onSnooze={snooze} onDismiss={dismiss} onDone={go}
      snoozes={reminderDefaults(src.current.settings).snooze.slice(0, 3)} chime={chime} />;
  }
  const link = current.source === 'approval' && !current.link ? { type: 'approval', id: current.id } : current.link;
  return <Alert r={current} more={queue.length - 1} onDone={done} onSnooze={snooze} onOpen={link ? open : null} onStart={start} onDismiss={dismiss}
    openLabel={targetOpenLabel(link) || 'Open'} target={link && link.type !== 'approval' ? describe(link) : null}
    snoozes={reminderDefaults(src.current.settings).snooze.slice(0, link || start ? 3 : 4)} chime={chime} />;
}

function Alert({ r, more, onDone, onSnooze, onOpen, onStart, onDismiss, openLabel, target, snoozes, chime, routine = null, onStartRoutine, onSkipRoutine }) {
  const full = r.style === 'fullscreen' || r.style === 'alarm' || r.source === 'approval';
  useChime(r, r.style === 'alarm' || r.source === 'approval' ? true : full, chime);
  useEffect(() => {
    // Desktop notification when the tab is away; the title flashes either way.
    let n = null;
    if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      n = new Notification(r.title || 'Reminder', { body: r.body || whenText(r.at), tag: `${r.id}|${r.at}`, requireInteraction: full, icon: '/pwa-192.png' });
      n.onclick = () => { window.focus(); n.close(); };
    }
    const base = document.title; let on = false;
    const iv = setInterval(() => { on = !on; document.title = on ? `⏰ ${r.title || 'Reminder'}` : base; }, 1000);
    return () => { clearInterval(iv); document.title = base; n?.close(); };
  }, [r, full]);
  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape') onDismiss(); if (e.key === 'Enter' && !e.target.closest?.('input,textarea,[contenteditable]')) onDone(); };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, [onDismiss, onDone]);

  const body = routine ? (
    <>
      <div className={s.kicker} style={{ color: routine.color }}><span className={s.bell} style={{ background: `color-mix(in srgb, ${routine.color} 18%, transparent)`, fontSize: full ? 24 : 18 }}>{routine.icon || '🔁'}</span>{routine.kicker || 'Routine'} · {whenText(r.at)}{more > 0 ? <span className={s.more}>+{more} more</span> : null}</div>
      <div className={s.title}>{r.title}</div>
      {r.body ? <div className={s.body}>{r.body}</div> : null}
      {routine.steps?.length ? <ol className={s.steps}>{routine.steps.map((x, i) => <li key={i}>{x}</li>)}</ol> : null}
      <div className={s.actions}>
        <button className={s.primary} style={{ background: routine.color }} onClick={onStartRoutine} autoFocus><Icon name="play" size={full ? 18 : 15} />Start routine</button>
        <button className={s.snooze} onClick={onSkipRoutine}>Not today</button>
        {snoozes.map((m) => <button key={m} className={s.snooze} onClick={() => onSnooze(m)} title={`Snooze ${m} min`}>{m >= 60 ? `${m / 60} h` : `${m} min`}</button>)}
        <button className={s.dismiss} onClick={onDismiss} title="Dismiss for later (Esc) — it goes to Reminders → Dismissed" aria-label="Dismiss for later"><Icon name="x" size={16} /></button>
      </div>
    </>
  ) : (
    <>
      <div className={s.kicker}><span className={s.bell}><Icon name="bell" size={full ? 22 : 16} /></span>{r.source === 'approval' ? 'Approval needed' : 'Reminder'} · {whenText(r.at)}{more > 0 ? <span className={s.more}>+{more} more</span> : null}</div>
      <div className={s.title}>{r.title || 'Reminder'}</div>
      {r.body ? <div className={s.body}>{r.body}</div> : null}
      {target ? <div className={s.target} data-missing={target.missing || undefined}><Icon name={target.icon} size={14} /><span>{target.label}</span>{target.sub ? <span className={s.targetSub}>{target.sub}</span> : null}</div> : null}
      <div className={s.actions}>
        {onOpen && !target?.missing ? <button className={s.primary} onClick={onOpen} autoFocus><Icon name="arrow-right" size={full ? 18 : 15} />{openLabel}</button> : null}
        {onStart ? <button className={s.start} onClick={onStart}><Icon name="play" size={full ? 16 : 14} />Start task</button> : null}
        {r.source !== 'approval' ? <button className={onOpen && !target?.missing ? s.snooze : s.primary} onClick={onDone} autoFocus={!onOpen || !!target?.missing}><Icon name="check" size={full ? 18 : 15} />Done</button> : null}
        {snoozes.map((m) => <button key={m} className={s.snooze} onClick={() => onSnooze(m)} title={`Snooze ${m} min`}>{m >= 60 ? `${m / 60} h` : `${m} min`}</button>)}
        <button className={s.dismiss} onClick={onDismiss} title="Dismiss for later (Esc) — closes on every device; it waits in Reminders → Dismissed" aria-label="Dismiss for later"><Icon name="x" size={16} /></button>
      </div>
    </>
  );
  return full
    ? <div className={s.takeover} role="alertdialog" aria-label={r.title}><div className={s.glow} style={routine ? { background: `radial-gradient(circle, color-mix(in srgb, ${routine.color} 30%, transparent), transparent 62%)` } : null} /><div className={s.card} style={routine ? { borderTopColor: routine.color } : null}>{body}</div></div>
    : <div className={s.banner} role="alert" style={routine ? { borderLeftColor: routine.color } : null}>{body}</div>;
}

function ApprovalStack({ items, chime, onClose, onReview, onDismiss }) {
  const { decide, approveAll } = useApprovals({ online: typeof navigator === 'undefined' || navigator.onLine !== false, live: false });
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const first = items[0];
  useChime(first, true, chime);
  useEffect(() => {
    let n = null;
    if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      n = new Notification(items.length > 1 ? `${items.length} approvals waiting` : first.title || 'Approval needed', { body: items.map((r) => r.approval?.preview || r.title).slice(0, 3).join(' · '), tag: 'ops-approvals', requireInteraction: true, icon: '/pwa-192.png' });
      n.onclick = () => { window.focus(); n.close(); };
    }
    return () => n?.close();
  }, [items.length]);
  const idOf = (r) => r.approval?.id || r.link?.id || r.id;
  const run = async (key, fn, done) => {
    setBusy(key); setErr(null);
    try { await fn(); onClose(done); } catch (e) { setErr(String(e?.message || e)); } finally { setBusy(null); }
  };
  const agents = [...new Set(items.map((r) => r.approval?.agent).filter(Boolean))];
  return (
    <div className={s.takeover} role="alertdialog" aria-label="Approvals needed">
      <div className={s.glow} />
      <div className={s.card}>
        <div className={s.kicker}><span className={s.bell}><Icon name="shield" size={22} /></span>
          {items.length > 1 ? `${items.length} approvals` : 'Approval needed'}{agents.length === 1 ? ` · ${agents[0]}` : ''} · {whenText(first.at)}</div>
        <div className={s.stack}>
          {items.map((r) => {
            const id = idOf(r);
            return (
              <div key={r.id} className={s.stackRow}>
                <div className={s.stackText}>
                  <div className={s.stackTitle}>{r.approval?.preview || r.title || 'Approval needed'}</div>
                  {r.approval?.note || r.body ? <div className={s.stackWhy}>{r.approval?.note || r.body}</div> : null}
                </div>
                <button className={s.snooze} disabled={!!busy} onClick={() => run(`${id}:reject`, () => decide(id, 'reject'), [r.id])}><Icon name="x" size={14} />{busy === `${id}:reject` ? 'Denying…' : 'Deny'}</button>
                <button className={s.primary} disabled={!!busy} onClick={() => run(`${id}:approve`, () => decide(id, 'approve'), [r.id])}><Icon name="check" size={14} />{busy === `${id}:approve` ? 'Approving…' : 'Approve'}</button>
              </div>
            );
          })}
        </div>
        {err ? <div className={s.body} style={{ color: 'var(--c-danger)' }}>{err}</div> : null}
        <div className={s.actions}>
          {items.length > 1 ? <button className={s.primary} disabled={!!busy} onClick={() => run('all', () => approveAll(items.map(idOf)), items.map((r) => r.id))}><Icon name="check-circle" size={16} />{busy === 'all' ? 'Approving…' : `Approve all ${items.length}`}</button> : null}
          <button className={s.snooze} onClick={onReview}><Icon name="arrow-right" size={15} />Review in Approvals</button>
          <button className={s.dismiss} onClick={onDismiss} title="Dismiss for later (Esc): they wait in Approvals" aria-label="Dismiss for later"><Icon name="x" size={16} /></button>
        </div>
      </div>
    </div>
  );
}

function whenText(at) { return new Date(at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }); }

/** A two-tone chime until answered (alarms repeat every 2.5 s, banners chime once). */
function useChime(r, loop, on = true) {
  useEffect(() => {
    if (!on) return undefined;
    let ctx; let iv;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const ring = () => {
        const t0 = ctx.currentTime;
        [[880, 0], [1320, 0.18], [880, 0.5], [1320, 0.68]].forEach(([f, dt]) => {
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.type = 'sine'; o.frequency.value = f;
          g.gain.setValueAtTime(0.0001, t0 + dt); g.gain.exponentialRampToValueAtTime(0.22, t0 + dt + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dt + 0.3);
          o.connect(g).connect(ctx.destination); o.start(t0 + dt); o.stop(t0 + dt + 0.32);
        });
      };
      ring();
      if (loop) iv = setInterval(ring, 2500);
    } catch { /* no audio */ }
    return () => { clearInterval(iv); ctx?.close().catch(() => {}); };
  }, [r, loop, on]);
}

/** For the Reminders page: ask once for desktop notifications (needs a click). */
export function useDesktopAlerts() {
  const [perm, setPerm] = useState(typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
  const ask = () => { if (typeof Notification !== 'undefined') Notification.requestPermission().then(setPerm); };
  return [perm, ask];
}
